import { Component, Inject, OnInit, ViewChild } from "@angular/core";
import { cosignDocumentsColumns } from "./co-sign.document.config";
import { ClientSideListBaseComponent } from "src/app/common/core/client-side-list-base/client-side-list-base.component";
import { CoSignerClient } from "src/app/common/services/co-signer.client";
import { StorageService } from "src/app/common/services/storage.service";
import { CoSignDetailsRequest, CoSignDocumentDetails, CoSignRequest } from "src/app/common/interfaces/cosign.interface";
import { Router } from "@angular/router";
import { SecurityFeatureUtility } from "src/app/common/services/security-feature.utility";
import { Features } from "src/app/common/enums/features.enum";
import { GetRowIdParams, IRowNode } from "ag-grid-community";
import { MatDialog } from "@angular/material/dialog";
import { ConfirmationModalComponent } from "src/app/common/components/core/modal/confirmation-modal/confirmation-modal.component";
import { CommonModule } from "@angular/common";
import { ChangesSaved, ErrorString, Ok, SomethingWentWrong } from "src/app/common/constants/core-constants";
import { CosignSearchComponent } from "../cosign-search/cosign-search.component";
import {
  isReactDocumentationEnabled,
  reactCoSignDocumentUrl,
} from "src/app/common/utils/react-documentation-route.util";

@Component({
  selector: "app-documents",
  templateUrl: "./documents.component.html",
  styleUrls: ["./documents.component.scss"],
  standalone: true,
  imports: [
    CommonModule,
    CosignSearchComponent,
    ClientSideListBaseComponent
],
})
export class DocumentsComponent implements OnInit {
  currentUser: string = "";
  loggedInUser: string | null = null;
  currentFacility: string | null = null;

  searchText: string = "";
  showArchived: boolean = false;
  enableSign: boolean = false;
  selectedRowsToSign: number[] = [];

  cosignPermissions: { canView: boolean, canManage: boolean } = {
    canView: false,
    canManage: false
  };

  columnDefs: any[] = [];
  cosignDocumentData: CoSignDocumentDetails[] = [];

  @ViewChild(ClientSideListBaseComponent)
  theraclient!: ClientSideListBaseComponent;

  constructor(
    @Inject("userId") public userId: string,
    private cosignClient: CoSignerClient,
    private storageService: StorageService,
    private router: Router,
    private securityFeatureUtility: SecurityFeatureUtility,
    private dialog: MatDialog,
  ) {
  }

  ngOnInit(): void {
    this.fetchCosignPermission();
    this.columnDefs = cosignDocumentsColumns(this).columnDefinitions;
    this.currentUser = this.userId;
    this.loggedInUser = this.storageService.getUserId();
    this.currentFacility = this.storageService.getSelectedFacilityId();
    this.getCoSignDocumentData(this.showArchived, this.searchText);
  }

  getCoSignDocumentData(showArchived: boolean, searchText: string): void {
    if (!this.currentUser || !this.currentFacility) {
      return;
    }

    const trimmedSearchValue = searchText.trim();

    const request: CoSignDetailsRequest = {
      facilityId: this.currentFacility,
      cosignerId: this.currentUser,
      archivedOnly: showArchived,
      searchString: trimmedSearchValue ? trimmedSearchValue : undefined
    };

    this.cosignClient
      .getCoSignDocumentDetails(request)
      .subscribe({
        next: (data: any) => {
          this.cosignDocumentData = data.responseData;
          this.syncSelections();
        },
        error: (error) => this.handleError(error)
      });
  }

  selectedForCoSign($event: any) {
    this.syncSelections();
  }

  private syncSelections(): void {
    const selectedRows: CoSignDocumentDetails[] = this.theraclient?.getSelectedRows() ?? [];

    this.selectedRowsToSign = selectedRows.map(x => x.id);
    this.enableSign =
      selectedRows.length > 0 &&
      selectedRows.every(x => x.isSignEnabled);
  }

  onSearch(value: string): void {
    this.searchText = value;
    this.getCoSignDocumentData(this.showArchived, this.searchText);
  }

  onShowArchived(value: boolean): void {
    this.showArchived = value;
    this.getCoSignDocumentData(this.showArchived, this.searchText);
  }

  onClickSign(): void {
    if (!this.loggedInUser || this.selectedRowsToSign.length === 0 || !this.enableSign) {
      return;
    }

    const cosignBillingRequest: CoSignRequest = {
      cosignedby: this.loggedInUser,
      ids: this.selectedRowsToSign,
    };

    this.cosignClient
      .signDocumentDetails(cosignBillingRequest)
      .subscribe(
        {
          next: (data: any) => this.handleSuccess(),
          error: (error) => this.handleError(error)
        });
  }

  navigateToDocumentsPage(params: any) {
    if (isReactDocumentationEnabled()) {
      window.location.href = reactCoSignDocumentUrl(
        params.data.facilityId,
        params.data.patientId,
        params.data.documentId,
        params.data.disciplineId,
      );
      return;
    }

    this.router.navigate(
      [
        "/patient",
        params.data.facilityId,
        "profile",
        params.data.patientId,
        "documentation",
        "documents",
      ],
      {
        queryParams: {
          documentid: params.data.documentId,
          disciplineid: params.data.disciplineId,
          frompage: "cosign",
        },
      }
    );
  }

  isRowSelectable = (params: IRowNode<CoSignDocumentDetails>) => {
    if (!params.data) return false;
    if (!this.cosignPermissions.canManage) return false;
    if (!params.data.isSignEnabled) return false;
    if (params.data.isArchived) return false;
    return params.data.isSignEnabled === true;
  }

  getRowId = (params: GetRowIdParams<CoSignDocumentDetails>): string => String(params.data?.id);

  private fetchCosignPermission(): void {
    this.cosignPermissions = this.securityFeatureUtility.getPermissions(Features.CO_SIGN);
  }

  private handleSuccess() {
    this.dialog.open(ConfirmationModalComponent, {
      data: ChangesSaved,
    })
      .afterClosed()
      .subscribe(() => {
        this.getCoSignDocumentData(this.showArchived, this.searchText);
      });
  }

  private handleError(error: any) {
    this.dialog.open(ConfirmationModalComponent, {
      data: {
        title: ErrorString,
        message: error?.error?.ResponseMessage ?? SomethingWentWrong,
        hideCancelButton: true,
        buttonText: Ok,
        isError: true
      }
    });
  };
}
